/**
 * Storefront API Helper for fetching delivery time metafields
 * This helper fetches product metafields via Shopify Storefront API GraphQL
 * when the data is not available in the DOM (e.g., adding from collection pages)
 */

window.StorefrontDeliveryHelper = (function() {
  // Storefront API configuration
  const STOREFRONT_ACCESS_TOKEN = '7e5a97d412b4a20f5e439d281a632341';
  const SHOP_DOMAIN = window.Shopify?.shop || window.location.hostname;
  const API_VERSION = '2024-01';
  const GRAPHQL_ENDPOINT = `https://${SHOP_DOMAIN}/api/${API_VERSION}/graphql.json`;

  // Cache for fetched data to avoid repeated API calls
  const cache = {};

  /**
   * Fetch product data including metafield via Storefront API
   * @param {string} productHandle - The product handle
   * @returns {Promise<Object>} Product data with metafield and variants
   */
  async function fetchProductDeliveryData(productHandle) {
    if (!productHandle) return null;

    // Check cache first
    const cacheKey = `product_${productHandle}`;
    if (cache[cacheKey]) {
      return cache[cacheKey];
    }

    const query = `
      query getProductDeliveryData($handle: String!) {
        product(handle: $handle) {
          id
          handle
          metafield(namespace: "custom", key: "availability_delivery_time") {
            value
          }
          variants(first: 100) {
            edges {
              node {
                id
                quantityAvailable
                availableForSale
                currentlyNotInStock
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch(GRAPHQL_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN
        },
        body: JSON.stringify({
          query: query,
          variables: { handle: productHandle }
        })
      });

      if (!response.ok) {
        console.warn('Storefront API request failed:', response.status);
        return null;
      }

      const data = await response.json();

      if (data.errors) {
        console.warn('Storefront API GraphQL errors:', data.errors);
        return null;
      }

      const product = data.data?.product;
      if (!product) {
        console.warn('Storefront API: Product not found for handle:', productHandle);
        return null;
      }

      // Format the data for easier use
      const result = {
        id: product.id,
        handle: product.handle,
        deliveryTimeMetafield: product.metafield?.value || '',
        variants: {}
      };

      // Debug log
      // console.log('✅ Storefront API fetched data for:', productHandle, {
      //   metafield: result.deliveryTimeMetafield,
      //   variantCount: product.variants?.edges?.length || 0
      // });

      // Process variants
      if (product.variants?.edges) {
        for (const edge of product.variants.edges) {
          const variant = edge.node;
          // Extract numeric ID from GID
          const variantId = variant.id.replace('gid://shopify/ProductVariant/', '');
          result.variants[variantId] = {
            quantityAvailable: variant.quantityAvailable || 0,
            availableForSale: variant.availableForSale,
            currentlyNotInStock: variant.currentlyNotInStock
          };
        }
      }

      // Cache the result
      cache[cacheKey] = result;

      return result;
    } catch (error) {
      console.warn('Storefront API fetch error:', error);
      return null;
    }
  }

  /**
   * Get delivery time text for a specific variant
   * @param {string} productHandle - The product handle
   * @param {string|number} variantId - The variant ID
   * @param {string} fastDeliveryText - Text to use when in stock
   * @param {string} defaultSlowDeliveryText - Default text if metafield is empty
   * @returns {Promise<Object>} Object with deliveryText and status
   */
  async function getVariantDeliveryInfo(productHandle, variantId, fastDeliveryText, defaultSlowDeliveryText) {
    fastDeliveryText = fastDeliveryText || 'Auf Lager. Lieferzeit 1-4 Werktage';
    defaultSlowDeliveryText = defaultSlowDeliveryText || 'Lieferzeit ca. 4 - 6 Wochen';

    const productData = await fetchProductDeliveryData(productHandle);

    if (!productData) {
      return {
        deliveryText: defaultSlowDeliveryText,
        status: 'unknown',
        metafield: ''
      };
    }

    const variantData = productData.variants[variantId];
    const metafield = productData.deliveryTimeMetafield;

    if (!variantData) {
      return {
        deliveryText: metafield || defaultSlowDeliveryText,
        status: 'unknown',
        metafield: metafield
      };
    }

    // Determine status based on availability
    // availableForSale = true means can be purchased (either in stock OR continue selling is ON)
    // currentlyNotInStock = true means inventory is 0 or less
    // quantityAvailable = actual stock count

    const inStock = variantData.quantityAvailable > 0;
    const canSellWhenOutOfStock = variantData.availableForSale && variantData.currentlyNotInStock;

    let result;
    if (inStock) {
      result = {
        deliveryText: fastDeliveryText,
        status: 'available',
        metafield: metafield,
        quantityAvailable: variantData.quantityAvailable
      };
    } else if (canSellWhenOutOfStock) {
      // Out of stock but continue selling is ON - use metafield
      result = {
        deliveryText: metafield || defaultSlowDeliveryText,
        status: 'incoming',
        metafield: metafield,
        quantityAvailable: variantData.quantityAvailable
      };
    } else {
      // Out of stock and can't sell
      result = {
        deliveryText: 'Nicht verfügbar',
        status: 'not_available',
        metafield: metafield,
        quantityAvailable: variantData.quantityAvailable
      };
    }

    // Debug log
    // console.log(`📦 Delivery info for variant ${variantId}:`, {
    //   status: result.status,
    //   deliveryText: result.deliveryText,
    //   metafield: result.metafield,
    //   qty: result.quantityAvailable
    // });

    return result;
  }

  /**
   * Get product handle from variant ID by checking the page or making API call
   * @param {string|number} variantId - The variant ID
   * @returns {Promise<string|null>} The product handle or null
   */
  async function getProductHandleFromVariant(variantId) {
    // First try to get from page
    const productLink = document.querySelector(`[href*="/products/"][href*="variant=${variantId}"]`);
    if (productLink) {
      const match = productLink.href.match(/\/products\/([^/?#]+)/);
      if (match) return match[1];
    }

    // Try to get from product form
    const productForm = document.querySelector('[action*="/cart/add"]');
    if (productForm) {
      const formAction = productForm.action || '';
      const productContainer = productForm.closest('[data-product-handle]');
      if (productContainer) {
        return productContainer.dataset.productHandle;
      }
    }

    // Try to get from canonical link
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      const match = canonical.href.match(/\/products\/([^/?#]+)/);
      if (match) return match[1];
    }

    // Try to get from URL
    const urlMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (urlMatch) return urlMatch[1];

    // Last resort: try to fetch from /products/search.json
    try {
      const searchRes = await fetch(`/search/suggest.json?q=variant:${variantId}&resources[type]=product&resources[limit]=1`);
      const searchData = await searchRes.json();
      if (searchData.resources?.results?.products?.[0]?.handle) {
        return searchData.resources.results.products[0].handle;
      }
    } catch(e) {}

    return null;
  }

  /**
   * Clear the cache
   */
  function clearCache() {
    for (const key in cache) {
      delete cache[key];
    }
  }

  // Public API
  return {
    fetchProductDeliveryData,
    getVariantDeliveryInfo,
    getProductHandleFromVariant,
    clearCache,
    STOREFRONT_ACCESS_TOKEN
  };
})();
